import {
  createContext,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { ThemeProvider, useTheme } from '@mui/material/styles';
import { useAppSelector } from '@shared/store';
import { createDesignVariantTheme } from '@shared/theme/designVariants';
import { designStorageKey, readDesignVariant, type DesignVariant } from './catalog';

type DesignContext = { variant: DesignVariant; selectVariant: (variant: DesignVariant) => void };
const Context = createContext<DesignContext>({ variant: 'main', selectVariant: () => {} });
export const useDesignVariant = () => useContext(Context);

export const DesignVariantProvider = ({
  children,
  enabled = true,
}: {
  children: ReactNode;
  enabled?: boolean;
}) => {
  const user = useAppSelector((state) => state.auth.authenticatedUser);
  const signedIn = useAppSelector((state) => Boolean(state.auth.accessToken));
  const scope =
    enabled && signedIn && user?.id && user.companyId
      ? JSON.stringify([user.companyId, user.id])
      : undefined;
  const [selection, setSelection] = useState(() => ({ scope, variant: readDesignVariant(scope) }));
  const variant = selection.scope === scope && scope ? selection.variant : 'main';
  useLayoutEffect(() => {
    setSelection({ scope, variant: readDesignVariant(scope) });
  }, [scope]);
  const parentTheme = useTheme();
  const theme = useMemo(
    () => createDesignVariantTheme(parentTheme, variant),
    [parentTheme, variant],
  );
  const value = useMemo<DesignContext>(
    () => ({
      variant,
      selectVariant: (next) => {
        if (!scope) return;
        setSelection({ scope, variant: next });
        try {
          sessionStorage.setItem(designStorageKey(scope), next);
        } catch {
          /* In-memory selection still works. */
        }
      },
    }),
    [scope, variant],
  );
  // Keep the provider and its children mounted across appearance changes, including Main.
  return (
    <Context.Provider value={value}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </Context.Provider>
  );
};
